/**
 * Pure, dependency-free resolver that turns a location's stored branding (widget theme, widget logo,
 * email / gift-voucher branding) into a single normalized `CheckoutBranding` object, plus the CSS
 * custom properties that skin the MultiSafepay Payment Component.
 *
 * This is intentionally framework-agnostic (no DOM, no React) so it can run in Cloud Functions and
 * in every frontend app (widget, giftVouchers, guest). Color derivation mirrors the widget theme
 * logic in apps/widget/src/utils/theme.ts so the checkout matches the rest of the booking flow.
 */

export interface CheckoutBranding {
  /** Primary brand color — buttons, focus rings, active borders. */
  accent: string;
  /** Contrasting text color to sit on top of `accent`. */
  accentText: string;
  /** Hover variant of `accent`. */
  accentHover: string;
  /** Translucent `accent` for soft fills (amount pill, selected method row). */
  accentSoft: string;
  /** Translucent `accent` for focus rings. */
  accentRing: string;
  /** Page / container background. */
  background: string;
  /** Elevated surface (the card the form sits on). */
  surface: string;
  /** Primary text color (derived for contrast against `surface`). */
  text: string;
  /** Muted / secondary text color. */
  textMuted: string;
  /** Border color for inputs and dividers. */
  border: string;
  /** Corner radius in px applied to inputs / container. */
  radius: number;
  /** CSS font-family stack. */
  fontFamily: string;
  /** Optional logo shown above the form. */
  logoUrl: string | null;
  /** Display name of the restaurant / location. */
  name: string | null;
  /** Pay / primary CTA fill — inverted on a saturated brand so it does not vanish. */
  cta: string;
  /** Text sitting on `cta`. */
  ctaText: string;
  /** Hairline / divider on top of `background`. */
  hairline: string;
}

const DEFAULT_ACCENT = '#fa7664';
const DEFAULT_BG = '#f5f6f8';
const DEFAULT_SURFACE = '#ffffff';
const DEFAULT_FONT =
  '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** Loose shape of the various branding sources we accept. All fields optional. */
export interface CheckoutBrandingInput {
  /** settings.widgetTheme */
  widgetTheme?: {
    accent?: string;
    brandBg?: string;
    fontFamily?: string;
  } | null;
  widgetLogoUrl?: string | null;
  /** settings.reservationEmails.branding / settings.giftVouchers.branding */
  emailBranding?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    logoUrl?: string;
    borderRadius?: number;
    fontFamily?: string;
  } | null;
  /** Restaurant / location display name. */
  name?: string | null;
}

function normalizeHex(input?: string | null): string | null {
  if (!input) return null;
  let hex = input.trim();
  if (!hex.startsWith('#')) hex = `#${hex}`;
  // Expand shorthand #abc -> #aabbcc
  if (/^#([a-f\d])([a-f\d])([a-f\d])$/i.test(hex)) {
    hex = '#' + hex.slice(1).split('').map((c) => c + c).join('');
  }
  return /^#[a-f\d]{6}$/i.test(hex) ? hex.toLowerCase() : null;
}

function toRgb(color: string): { r: number; g: number; b: number } {
  const hex = normalizeHex(color);
  if (hex) {
    return {
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16),
    };
  }
  const m = color.match(/\d+/g);
  if (m && m.length >= 3) {
    return { r: +m[0], g: +m[1], b: +m[2] };
  }
  return { r: 250, g: 118, b: 100 };
}

/** Relative luminance (0 dark – 1 light). */
function luminance(color: string): number {
  const { r, g, b } = toRgb(color);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function isLight(color: string): boolean {
  // Match the widget frame (threshold 0.7). Saturated brands like #fa7664 sit
  // around 0.61 and must keep white type + glass cards, not a white sheet.
  return luminance(color) > 0.7;
}

/** Contrasting text color (near-black or white) for a given background. */
export function getContrastColor(bg: string): string {
  return isLight(bg) ? '#111827' : '#ffffff';
}

/** Slightly darken light colors / lighten dark colors for hover states. */
export function deriveHoverColor(color: string): string {
  let { r, g, b } = toRgb(color);
  if (isLight(color)) {
    r = Math.max(0, Math.floor(r * 0.85));
    g = Math.max(0, Math.floor(g * 0.85));
    b = Math.max(0, Math.floor(b * 0.85));
  } else {
    r = Math.min(255, Math.floor(r + (255 - r) * 0.15));
    g = Math.min(255, Math.floor(g + (255 - g) * 0.15));
    b = Math.min(255, Math.floor(b + (255 - b) * 0.15));
  }
  return `rgb(${r}, ${g}, ${b})`;
}

/** Same color at `alpha` opacity — soft fills and focus rings derived from the accent. */
export function withAlpha(color: string, alpha: number): string {
  const { r, g, b } = toRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Mix `color` toward `target` by `amount` (0–1). Used to derive muted text / borders. */
function mix(color: string, target: string, amount: number): string {
  const a = toRgb(color);
  const b = toRgb(target);
  const r = Math.round(a.r + (b.r - a.r) * amount);
  const g = Math.round(a.g + (b.g - a.g) * amount);
  const bl = Math.round(a.b + (b.b - a.b) * amount);
  return `rgb(${r}, ${g}, ${bl})`;
}

/**
 * Resolve a location's stored branding into a normalized `CheckoutBranding`.
 * Precedence: widget theme (the customer-facing brand) wins for accent/background/font; email/gift
 * branding fills gaps and provides logo + radius; sensible 10Seat defaults otherwise.
 */
export function resolveCheckoutBranding(input: CheckoutBrandingInput = {}): CheckoutBranding {
  const theme = input.widgetTheme || {};
  const email = input.emailBranding || {};

  const accent =
    normalizeHex(theme.accent) || normalizeHex(email.primaryColor) || DEFAULT_ACCENT;
  const background =
    normalizeHex(theme.brandBg) || normalizeHex(email.backgroundColor) || DEFAULT_BG;
  const brandIsLight = isLight(background);
  // On a saturated widget brand, cards are the same frosted glass as time slots / continue.
  // On a light brand, keep a solid white surface so the hosted page stays a normal checkout.
  const surface = brandIsLight ? DEFAULT_SURFACE : withAlpha('#ffffff', 0.18);
  // Body copy must contrast the brand frame, not a forced white card (email textColor is
  // for mail headers and is often white — that made the widget checkout unreadable).
  const text = getContrastColor(background);
  const fontFamily = theme.fontFamily || email.fontFamily || DEFAULT_FONT;
  const radius =
    typeof email.borderRadius === 'number' && email.borderRadius >= 0 ? email.borderRadius : 10;
  const logoUrl = input.widgetLogoUrl || email.logoUrl || null;
  const cta = brandIsLight ? accent : 'rgba(255, 255, 255, 0.92)';
  const ctaText = brandIsLight ? getContrastColor(accent) : accent;

  return {
    accent,
    accentText: getContrastColor(accent),
    accentHover: deriveHoverColor(accent),
    accentSoft: withAlpha(accent, 0.12),
    accentRing: withAlpha(accent, 0.22),
    background,
    surface,
    text,
    textMuted: brandIsLight ? mix(text, background, 0.45) : 'rgba(255, 255, 255, 0.75)',
    border: brandIsLight ? mix(text, background, 0.82) : 'rgba(255, 255, 255, 0.28)',
    hairline: brandIsLight ? mix(text, background, 0.9) : 'rgba(255, 255, 255, 0.16)',
    cta,
    ctaText,
    radius,
    fontFamily,
    logoUrl,
    name: input.name || null,
  };
}

/**
 * CSS custom properties consumed by shared/components/payments/multisafepayCheckout.css to skin the
 * MultiSafepay component (`.msp-*` classes) and the surrounding checkout shell.
 */
export function checkoutBrandingCssVars(b: CheckoutBranding): Record<string, string> {
  return {
    '--ck-accent': b.accent,
    '--ck-accent-text': b.accentText,
    '--ck-accent-hover': b.accentHover,
    '--ck-accent-soft': b.accentSoft,
    '--ck-accent-ring': b.accentRing,
    '--ck-cta': b.cta,
    '--ck-cta-text': b.ctaText,
    '--ck-bg': b.background,
    '--ck-surface': b.surface,
    '--ck-text': b.text,
    '--ck-text-muted': b.textMuted,
    '--ck-border': b.border,
    '--ck-hairline': b.hairline,
    '--ck-radius': `${b.radius}px`,
    '--ck-font': b.fontFamily,
  };
}
